import React, { useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";

import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import {
  CommuneDTO,
  DistrictDTO,
  FokontanyDTO,
  RegionDTO,
  searchAutoComplete,
} from "@utils/appUtils.ts";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import {
  incidentLocationOptions,
  objectLists,
  StepCallBack,
} from "@modules/plaintes/plaintes.model.ts";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  communeList,
  districtList,
  fokontanyList,
  loadingDistrict,
  loadingLocalisation,
  loadingCommune,
  loadingFokotany,
  regionList,
} from "@core/slice/LocalisationSlice.ts";
import {
  getAllRegions,
  getCommuneByDistrict,
  getDistrictByRegion,
  getFokontanysByCommune,
} from "@core/actions/localisationAction.ts";
import {
  setStepReasonValue,
  stepReasonValue,
} from "@core/slice/ComplaintSlice.ts";
import { FaRegClock } from "react-icons/fa";
import { FetchStatusType } from "@core/modeles/model.ts";
import { addLocale } from "primereact/api";
import { userConnected } from "@core/slice/UsersSlice.ts";
import { createDynamicOptions, fetchStandardDynamicOptions } from "@core/actions/dynamicOptionAction.ts";
import { DynamicOptionCategory, BACKEND_SUPPORTED_CATEGORIES } from "@core/modeles/dynamicOption.model.ts";
import {
  incrementDynamicOption,
  makeSelectStandardLabels,
  STANDARD_THRESHOLD,
  makeCountKey,
} from "@core/slice/dynamicOptionsCountSlice.ts";

const CaseDetail: React.FC<StepCallBack> = ({
  onNext,
  onPrevious,
  isResolvedComplaint,
}) => {
  const dispatch = useAppDispatch();
  const detailsUserConnected = useAppSelector(userConnected);
  const dataRegion = useAppSelector(regionList);

  // ── Options standard issues du comptage frontend ─────────────────────────
  const selectStandardIncidentLocations = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.INCIDENT_LOCATION),
    []
  );
  const standardIncidentLabels = useAppSelector(selectStandardIncidentLocations);
  const dynamicCounts = useAppSelector(
    (state: any) => state.dynamicOptionsCount?.counts ?? {}
  );
  const allIncidentLocationOptions = useMemo(() => {
    const staticNames = new Set(incidentLocationOptions.map((o) => o.name.toLowerCase()));
    const extra = standardIncidentLabels
      .filter((label) => !staticNames.has(label.toLowerCase()))
      .map((label) => ({ name: label, code: `CUSTOM_${label.toLowerCase()}` }));
    return [...incidentLocationOptions, ...extra];
  }, [standardIncidentLabels]);
  // ───────────────────────────────────────────────────────

  const dataDistrict = useAppSelector(districtList);
  const dataCommune = useAppSelector(communeList);
  const stepReason = useAppSelector(stepReasonValue);
  const dataFokontany = useAppSelector(fokontanyList);
  const loadingRegion = useAppSelector(loadingLocalisation);
  const districtLoader = useAppSelector(loadingDistrict);
  const communeLoader = useAppSelector(loadingCommune);
  const fokotanyLoader = useAppSelector(loadingFokotany);
  const [errors, setErrors] = useState({
    description: "",
    incidentDate: "",
    incidentHour: "",
    location: "",
    fokontany: "",
    region: "",
    district: "",
    commune: "",
    otherLocation: "",
    object: ""
  });

  const autoCompleteRefs = useRef<{ [key: string]: any }>({
    region: null,
    district: null,
    commune: null,
    fokontany: null,
  });

  const [filteredRegions, setFilteredRegions] = useState<RegionDTO[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<DistrictDTO[]>([]);
  const [filteredCommunes, setFilteredCommunes] = useState<CommuneDTO[]>([]);
  const [filteredFokontanys, setFilteredFokontanys] = useState<FokontanyDTO[]>(
    []
  );

  const [selectedRegion, setSelectedRegion] = useState<RegionDTO | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictDTO | null>(
    null
  );
  const [selectedCommune, setSelectedCommune] = useState<CommuneDTO | null>(
    null
  );
  const [selectedFokontany, setSelectedFokontany] =
    useState<FokontanyDTO | null>(null);

  const isInternalAgent =
    detailsUserConnected &&
    detailsUserConnected.profil.accesses[0].internalAgent;

  addLocale("fr", {
    firstDayOfWeek: 1,
    dayNames: [
      "dimanche",
      "lundi",
      "mardi",
      "mercredi",
      "jeudi",
      "vendredi",
      "samedi",
    ],
    dayNamesShort: ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
    dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
    monthNames: [
      "Janvier ",
      "Février ",
      "Mars ",
      "Avril ",
      "Mai ",
      "Juin ",
      "Juillet ",
      "Août ",
      "Septembre ",
      "Octobre ",
      "Novembre ",
      "Décembre ",
    ],
    monthNamesShort: [
      "janv",
      "févr",
      "mars",
      "avr",
      "mai",
      "juin",
      "juil",
      "août",
      "sept",
      "oct",
      "nov",
      "déc",
    ],
    today: "Aujourd'hui",
    clear: "Effacer",
  });

  React.useEffect(() => {
    if (stepReason && stepReason.stepThree) {
      setSelectedRegion(stepReason.stepThree.region ?? null);
      setSelectedDistrict(stepReason.stepThree.district ?? null);
      setSelectedCommune(stepReason.stepThree.commune ?? null);
      setSelectedFokontany(stepReason.stepThree.fokotany ?? null);
    }
  }, [stepReason]);

  React.useEffect(() => {
    setFilteredRegions(dataRegion);
  }, [dataRegion]);

  React.useEffect(() => {
    dispatch(
      getAllRegions({
        page: 1,
        size: 22,
      })
    );
    // GET standard options pour INCIDENT_LOCATION (supporté par le backend)
    if (BACKEND_SUPPORTED_CATEGORIES.has(DynamicOptionCategory.INCIDENT_LOCATION)) {
      dispatch(fetchStandardDynamicOptions(DynamicOptionCategory.INCIDENT_LOCATION));
    }
  }, [dispatch]);
  const handleNextStep = () => {
    let formErrors = { ...errors };
    if (stepReason?.stepThree === null) {
      formErrors.description = "Veuillez remplir la description du cas";
      formErrors.incidentDate = "Veuillez remplir la date de l'incident";
      formErrors.location = "Veuillez remplir le lieu de l'incident";
      formErrors.fokontany = "Veuillez remplir le Fokontany";
      formErrors.region = "Veuillez remplir la région";
      formErrors.commune = "Veuillez remplir la commune";
      formErrors.district = "Veuillez remplir le district";
      formErrors.object = "Veuillez remplir l'objet de la plainte";
      setErrors(formErrors);
    } else {
      if (
        stepReason &&
        stepReason.stepThree &&
        stepReason.stepThree.description.trim() === ""
      ) {
        formErrors.description = "Veuillez remplir la description du cas";
      } else {
        formErrors.description = "";
      }

      if (
        stepReason &&
        stepReason.stepThree &&
        !stepReason.stepThree.object
      ) {
        formErrors.object = "Veuillez remplir l'objet de la plainte";
      } else {
        formErrors.object = "";
      }

      if (
        stepReason &&
        stepReason.stepThree &&
        !stepReason.stepThree.incidentDate
      ) {
        formErrors.incidentDate = "Veuillez remplir la date de l'incident";
      } else {
        formErrors.incidentDate = "";
      }

      if (
        stepReason &&
        stepReason.stepThree &&
        !stepReason.stepThree.location
      ) {
        formErrors.location = "Veuillez remplir le lieu de l'incident";
      } else {
        formErrors.location = "";
      }

      if (
        stepReason &&
        stepReason.stepThree &&
        !stepReason.stepThree.fokontanyId
      ) {
        formErrors.fokontany = "Veuillez remplir le Fokontany";
      } else {
        formErrors.fokontany = "";
      }
      if (stepReason && stepReason.stepThree && !stepReason.stepThree.region) {
        formErrors.region = "Veuillez remplir la région";
      } else {
        formErrors.region = "";
      }
      if (stepReason && stepReason.stepThree && !stepReason.stepThree.commune) {
        formErrors.commune = "Veuillez remplir la commune";
      } else {
        formErrors.commune = "";
      }
      if (
        stepReason &&
        stepReason.stepThree &&
        !stepReason.stepThree.district
      ) {
        formErrors.district = "Veuillez remplir le district";
      } else {
        formErrors.district = "";
      }
      if (
        stepReason &&
        stepReason.stepThree &&
        stepReason.stepThree?.location.name === "Autres"
      ) {
        if (stepReason.stepThree.otherLocation === undefined) {
          formErrors.otherLocation =
            "Veuillez remplir un autre lieu d'incident";
        } else {
          formErrors.otherLocation = "";
        }
      }

      setErrors(formErrors);
      if (
        !formErrors.description &&
        !formErrors.incidentDate &&
        !formErrors.location &&
        !formErrors.fokontany &&
        !formErrors.region &&
        !formErrors.commune &&
        !formErrors.district &&
        !formErrors.otherLocation &&
        !formErrors.object
      ) {
        if (stepReason) {
          // ── POST /dynamic-option : 1 occurrence par soumission
          //    → visible dans options seulement après 3 soumissions distinctes (backend: occurrenceCount >= 3 → standard:true)
          if (
            stepReason.stepThree?.location?.name === "Autres" &&
            stepReason.stepThree?.otherLocation?.trim()
          ) {
            const otherLocVal = stepReason.stepThree.otherLocation.trim();

            // ── Comptage frontend ───────────────────────────────────
            dispatch(incrementDynamicOption({
              category: DynamicOptionCategory.INCIDENT_LOCATION,
              label: otherLocVal,
            }));
            // Calcul du nouveau compteur (optimiste : +1)
            // const currentCount = (dynamicCounts[makeCountKey(DynamicOptionCategory.INCIDENT_LOCATION, otherLocVal)] ?? 0) + 1;
            // const remaining = Math.max(0, STANDARD_THRESHOLD - currentCount);
            // if (remaining === 0) {
            //   console.log(`[CaseDetail] ✅ Front comptage "${otherLocVal}" [${currentCount}/${STANDARD_THRESHOLD}] → VISIBLE dans dropdown`);
            // } else {
            //   console.log(`[CaseDetail] ⏳ Front comptage "${otherLocVal}" [${currentCount}/${STANDARD_THRESHOLD}] → encore ${remaining} soumission(s) requise(s)`);
            // }

            // ── POST /dynamic-option pour INCIDENT_LOCATION (supporté par le backend) ─
            // natureId: null car INCIDENT_LOCATION n'est pas lié à une sous-nature
            if (BACKEND_SUPPORTED_CATEGORIES.has(DynamicOptionCategory.INCIDENT_LOCATION)) {
              dispatch(
                createDynamicOptions([
                  { labelOriginal: otherLocVal, category: DynamicOptionCategory.INCIDENT_LOCATION, natureId: null },
                ])
              );
            }
            //  else {
            //   console.warn(
            //     `[CaseDetail] ⏭️ POST /dynamic-option ignoré pour INCIDENT_LOCATION "${otherLocVal}" (backend non supporté)`
            //   );
            // }
          }
          // ─────────────────────────────────────────────────────────────────
         
          dispatch(setStepReasonValue(stepReason));
          onNext();
        }
      }
    }
  };

  function handlePreviousStep() {
    if (onPrevious) {
      onPrevious();
    }
  }

  const handleChangeIncident = (e: DropdownChangeEvent) => {
    if (e.value.name !== "Autres") {
      setErrors({ ...errors, otherLocation: "" });
    }
    if (stepReason) {
      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepThree: {
            location: e.value,
            description: stepReason.stepThree?.description ?? "",
            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
            incidentDate: stepReason.stepThree?.incidentDate ?? new Date(),
            incidentHour: stepReason.stepThree?.incidentHour ?? "",
            priority: stepReason.stepThree?.priority ?? "",
            receivedDate: stepReason.stepThree?.receivedDate ?? "",
            reportId: stepReason.stepThree?.reportId ?? "",
            status: stepReason.stepThree?.status ?? "",
            userUuid: stepReason.stepThree?.userUuid ?? "",
            region: stepReason.stepThree?.region,
            district: stepReason.stepThree?.district,
            commune: stepReason.stepThree?.commune,
            fokotany: stepReason.stepThree?.fokotany,
            object: stepReason.stepThree?.object,
            otherLocation:
              e.value.name !== "Autres"
                ? ""
                : stepReason.stepThree?.otherLocation,
          },
        })
      );
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      location: "",
    }));
  };

  React.useEffect(() => {
    if (selectedRegion?.id) {
      const idRegion = selectedRegion.id;
      dispatch(
        getDistrictByRegion({
          page: 1,
          size: -1,
          region: idRegion.toString(),
        })
      );
    }
  }, [dispatch, selectedRegion]);

  React.useEffect(() => {
    if (selectedDistrict?.id) {
      const idDistrict = selectedDistrict.id;
      dispatch(
        getCommuneByDistrict({
          page: 1,
          size: -1,
          district: idDistrict.toString(),
        })
      );
    }
  }, [dispatch, selectedDistrict]);

  React.useEffect(() => {
    if (selectedCommune?.id) {
      const idCommune = selectedCommune.id;
      dispatch(
        getFokontanysByCommune({
          page: 1,
          size: -1,
          commune: idCommune.toString(),
        })
      );
    }
  }, [dispatch, selectedCommune]);

  // Mise à jour des districts en fonction de la région sélectionnée
  const handleChangeRegion = (e: AutoCompleteChangeEvent) => {
    const selectedData = e.value;
    setSelectedRegion(selectedData);
    setSelectedDistrict(null);
    setSelectedCommune(null);
    setSelectedFokontany(null);
    if (stepReason) {
      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepThree: {
            location: stepReason.stepThree?.location ?? "",
            description: stepReason.stepThree?.description ?? "",
            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
            incidentDate: stepReason.stepThree?.incidentDate ?? new Date(),
            incidentHour: stepReason.stepThree?.incidentHour ?? "",
            priority: stepReason.stepThree?.priority ?? "",
            receivedDate: stepReason.stepThree?.receivedDate ?? "",
            reportId: stepReason.stepThree?.reportId ?? "",
            status: stepReason.stepThree?.status ?? "",
            userUuid: stepReason.stepThree?.userUuid ?? "",
            region: selectedData,
            otherLocation: stepReason.stepThree?.otherLocation,
            object: stepReason.stepThree?.object,
          },
        })
      );
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      region: "",
    }));
  };

  // Mise à jour des communes en fonction du district sélectionné
  const handleChangeDistrict = (e: any) => {
    const selectedDistrict = e.value;
    setSelectedDistrict(selectedDistrict);
    setSelectedCommune(null);
    setSelectedFokontany(null);
    if (stepReason) {
      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepThree: {
            location: stepReason.stepThree?.location ?? "",
            description: stepReason.stepThree?.description ?? "",
            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
            incidentDate: stepReason.stepThree?.incidentDate ?? new Date(),
            incidentHour: stepReason.stepThree?.incidentHour ?? "",
            priority: stepReason.stepThree?.priority ?? "",
            receivedDate: stepReason.stepThree?.receivedDate ?? "",
            reportId: stepReason.stepThree?.reportId ?? "",
            status: stepReason.stepThree?.status ?? "",
            userUuid: stepReason.stepThree?.userUuid ?? "",
            region: stepReason.stepThree?.region,
            district: selectedDistrict,
            otherLocation: stepReason.stepThree?.otherLocation,
            object: stepReason.stepThree?.object,
          },
        })
      );
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      district: "",
    }));
  };

  // Mise à jour des fokontany en fonction de la commune sélectionnée
  const handleChangeCommune = (e: any) => {
    const selectedCommuneCode = e.value;
    setSelectedCommune(selectedCommuneCode);
    setSelectedFokontany(null);
    if (stepReason) {
      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepThree: {
            location: stepReason.stepThree?.location ?? "",
            description: stepReason.stepThree?.description ?? "",
            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
            incidentDate: stepReason.stepThree?.incidentDate ?? new Date(),
            incidentHour: stepReason.stepThree?.incidentHour ?? "",
            priority: stepReason.stepThree?.priority ?? "",
            receivedDate: stepReason.stepThree?.receivedDate ?? "",
            reportId: stepReason.stepThree?.reportId ?? "",
            status: stepReason.stepThree?.status ?? "",
            userUuid: stepReason.stepThree?.userUuid ?? "",
            region: stepReason.stepThree?.region,
            district: stepReason.stepThree?.district,
            commune: selectedCommuneCode,
            otherLocation: stepReason.stepThree?.otherLocation,
            object: stepReason.stepThree?.object,
          },
        })
      );
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      commune: "",
    }));
  };

  const handleChangeFokontany = (e: DropdownChangeEvent) => {
    const fokontanyId = e.value;
    setSelectedFokontany(e.value);
    if (stepReason) {
      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepThree: {
            location: stepReason.stepThree?.location ?? "",
            description: stepReason.stepThree?.description ?? "",
            fokontanyId: fokontanyId.id,
            incidentDate: stepReason.stepThree?.incidentDate ?? new Date(),
            incidentHour: stepReason.stepThree?.incidentHour ?? "",
            priority: stepReason.stepThree?.priority ?? "",
            receivedDate: stepReason.stepThree?.receivedDate ?? "",
            reportId: stepReason.stepThree?.reportId ?? "",
            status: stepReason.stepThree?.status ?? "",
            userUuid: stepReason.stepThree?.userUuid ?? "",
            region: stepReason.stepThree?.region,
            district: stepReason.stepThree?.district,
            commune: stepReason.stepThree?.commune,
            fokotany: fokontanyId,
            otherLocation: stepReason.stepThree?.otherLocation,
            object: stepReason.stepThree?.object,
          },
        })
      );
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      fokontany: "",
    }));
  };

  const handleFocus = (
    type: string,
    data: any,
    setState: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    if (data.length > 0) {
      setState(data);
    }

    const ref = autoCompleteRefs.current[type];
    if (ref) {
      ref.show();
    }
  };
  return (
    <div className="column gap-[20px] h-12rem card-stepper">
      <div className="stitle">Description du cas</div>
      {stepReason && (
        <div className="column flex flex-column gap-[10px] ">
          <div className="flex flex-wrap gap-3">
            <div className="column gap-2 w-full">
              <label htmlFor="username">
                Explication de la plainte (obligatoire)
              </label>
              <InputTextarea
                placeholder="A remplir par le CMS ou IS"
                autoResize
                value={stepReason.stepThree?.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  if (stepReason) {
                    dispatch(
                      setStepReasonValue({
                        ...stepReason,
                        stepThree: {
                          location: stepReason.stepThree?.location ?? "",
                          description: e.target.value,
                          fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
                          incidentDate:
                            stepReason.stepThree?.incidentDate ?? new Date(),
                          incidentHour:
                            stepReason.stepThree?.incidentHour ?? "",
                          priority: stepReason.stepThree?.priority ?? "",
                          receivedDate:
                            stepReason.stepThree?.receivedDate ?? "",
                          reportId: stepReason.stepThree?.reportId ?? "",
                          status: stepReason.stepThree?.status ?? "",
                          userUuid: stepReason.stepThree?.userUuid ?? "",
                          region: stepReason.stepThree?.region,
                          district: stepReason.stepThree?.district,
                          commune: stepReason.stepThree?.commune,
                          fokotany: stepReason.stepThree?.fokotany,
                          otherLocation: stepReason.stepThree?.otherLocation,
                          object: stepReason.stepThree?.object,
                        },
                      })
                    );
                  }
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    description: "",
                  }));
                }}
                rows={5}
                cols={30}
                className="min-h-[80px]"
              />
              {errors.description && (
                <small className="text-red-600">{errors.description}</small>
              )}
            </div>
          </div>
          {isInternalAgent && (
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="object">
                  Objet de la plainte (obligatoire)
                </label>
                <Dropdown
                  showClear={true}
                  className="w-full"
                  options={objectLists}
                  value={stepReason.stepThree?.object}
                  onChange={(e) => {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      object: "",
                    }));
                    dispatch(
                      setStepReasonValue({
                        ...stepReason,
                        stepThree: {
                          location: stepReason.stepThree?.location ?? "",
                          description: stepReason.stepThree?.description ?? "",
                          fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
                          incidentDate:
                            stepReason.stepThree?.incidentDate ?? new Date(),
                          incidentHour:
                            stepReason.stepThree?.incidentHour ?? "",
                          priority: stepReason.stepThree?.priority ?? "",
                          receivedDate:
                            stepReason.stepThree?.receivedDate ?? "",
                          reportId: stepReason.stepThree?.reportId ?? "",
                          status: stepReason.stepThree?.status ?? "",
                          userUuid: stepReason.stepThree?.userUuid ?? "",
                          region: stepReason.stepThree?.region,
                          district: stepReason.stepThree?.district,
                          commune: stepReason.stepThree?.commune,
                          fokotany: stepReason.stepThree?.fokotany,
                          otherLocation: stepReason.stepThree?.otherLocation,
                          object: e.value,
                        },
                      })
                    );
                  }}
                  placeholder="Selectionner l'objet de la plainte"
                  optionLabel="name"
                />
              </div>
              {errors.object && (
                <small className="text-red-600">{errors.object}</small>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 ">
            <div className="flex flex-wrap gap-3">
              <div className="flex-auto column gap-2 w-full">
                <label htmlFor="date">
                  Date de l'incident ou de la situation (obligatoire)
                </label>
                <Calendar
                  locale="fr"
                  placeholder="dd/mm/yyyy"
                  id="buttondisplay"
                  value={
                    stepReason.stepThree?.incidentDate
                      ? new Date(stepReason.stepThree.incidentDate)
                      : null
                  }
                  onChange={(e) => {
                    if (stepReason) {
                      dispatch(
                        setStepReasonValue({
                          ...stepReason,
                          stepThree: {
                            location: stepReason.stepThree?.location ?? "",
                            description:
                              stepReason.stepThree?.description ?? "",
                            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
                            incidentDate: e.value ?? new Date(),
                            incidentHour: "",
                            priority: stepReason.stepThree?.priority ?? "",
                            receivedDate:
                              stepReason.stepThree?.receivedDate ?? "",
                            reportId: stepReason.stepThree?.reportId ?? "",
                            status: stepReason.stepThree?.status ?? "",
                            userUuid: stepReason.stepThree?.userUuid ?? "",
                            region: stepReason.stepThree?.region,
                            district: stepReason.stepThree?.district,
                            commune: stepReason.stepThree?.commune,
                            fokotany: stepReason.stepThree?.fokotany,
                            otherLocation: stepReason.stepThree?.otherLocation,
                            object: stepReason.stepThree?.object,
                          },
                        })
                      );
                    }
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      incidentDate: "",
                    }));
                  }}
                  showIcon
                  dateFormat="dd/mm/yy"
                  maxDate={new Date()}
                />
                {errors.incidentDate && (
                  <small className="text-red-600">{errors.incidentDate}</small>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="flex-auto column gap-2 w-full">
                <label htmlFor="calendar-timeonly">
                  Heure de l’incident ou de la situation
                </label>
                <Calendar
                  placeholder="00:00"
                  id="buttondisplay"
                  value={
                    stepReason.stepThree?.incidentHour
                      ? new Date(stepReason.stepThree.incidentHour)
                      : null
                  }
                  onChange={(e) => {
                    if (stepReason) {
                      const incidentDate = stepReason.stepThree?.incidentDate
                        ? new Date(stepReason.stepThree.incidentDate)
                        : new Date();
                      const selectedTime = e.value
                        ? new Date(e.value)
                        : new Date();
                      incidentDate.setHours(
                        selectedTime.getHours(),
                        selectedTime.getMinutes(),
                        selectedTime.getSeconds()
                      );
                      dispatch(
                        setStepReasonValue({
                          ...stepReason,
                          stepThree: {
                            location: stepReason.stepThree?.location ?? "",
                            description:
                              stepReason.stepThree?.description ?? "",
                            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
                            incidentDate: e.value
                              ? incidentDate
                              : stepReason.stepThree?.incidentDate!,
                            incidentHour: e.value ?? "",
                            priority: stepReason.stepThree?.priority ?? "",
                            receivedDate:
                              stepReason.stepThree?.receivedDate ?? "",
                            reportId: stepReason.stepThree?.reportId ?? "",
                            status: stepReason.stepThree?.status ?? "",
                            userUuid: stepReason.stepThree?.userUuid ?? "",
                            region: stepReason.stepThree?.region,
                            district: stepReason.stepThree?.district,
                            commune: stepReason.stepThree?.commune,
                            fokotany: stepReason.stepThree?.fokotany,
                            otherLocation: stepReason.stepThree?.otherLocation,
                            object: stepReason.stepThree?.object,
                          },
                        })
                      );
                    }
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      incidentHour: "",
                    }));
                  }}
                  timeOnly
                  showIcon
                  icon={<FaRegClock />}
                  maxDate={(() => {
                    const incidentDate = stepReason.stepThree?.incidentDate
                      ? new Date(stepReason.stepThree.incidentDate)
                      : null;
                    const now = new Date();
                    if (
                      incidentDate &&
                      incidentDate.getDate() === now.getDate() &&
                      incidentDate.getMonth() === now.getMonth() &&
                      incidentDate.getFullYear() === now.getFullYear()
                    ) {
                      return now;
                    }

                    return undefined;
                  })()}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="username">
                  Lieu de l'incident ou de la situation (obligatoire)
                </label>
                <Dropdown
                  name="role"
                  appendTo={document.body}
                  value={stepReason.stepThree?.location ?? null}
                  options={allIncidentLocationOptions}
                  optionLabel="name"
                  className="w-full"
                  placeholder="---Lieu de l'incident---"
                  onChange={(e) => handleChangeIncident(e)}
                />
                {errors.location && (
                  <small className="text-red-600">{errors.location}</small>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="autre">
                  Si autre, mentionner le lieu de l'incident{" "}
                  {stepReason &&
                  stepReason.stepThree?.location.name === "Autres"
                    ? "(obligatoire)"
                    : ""}
                </label>
                <InputText
                  maxLength={255}
                  id="autre"
                  placeholder=""
                  value={stepReason.stepThree?.otherLocation ?? ""}
                  onChange={(e) => {
                    if (stepReason) {
                      setErrors({ ...errors, otherLocation: "" });
                      dispatch(
                        setStepReasonValue({
                          ...stepReason,
                          stepThree: {
                            location: stepReason.stepThree?.location ?? "",
                            description:
                              stepReason.stepThree?.description ?? "",
                            fokontanyId: stepReason.stepThree?.fokontanyId ?? 0,
                            incidentDate:
                              stepReason.stepThree?.incidentDate ?? new Date(),
                            incidentHour:
                              stepReason.stepThree?.incidentHour ?? "",
                            priority: stepReason.stepThree?.priority ?? "",
                            receivedDate:
                              stepReason.stepThree?.receivedDate ?? "",
                            reportId: stepReason.stepThree?.reportId ?? "",
                            status: stepReason.stepThree?.status ?? "",
                            userUuid: stepReason.stepThree?.userUuid ?? "",
                            region: stepReason.stepThree?.region,
                            district: stepReason.stepThree?.district,
                            commune: stepReason.stepThree?.commune,
                            fokotany: stepReason.stepThree?.fokotany,
                            otherLocation: e.target.value,
                            object: stepReason.stepThree?.object,
                          },
                        })
                      );
                    }
                  }}
                  disabled={
                    stepReason &&
                    stepReason.stepThree?.location.name !== "Autres"
                  }
                />
                {errors.otherLocation && (
                  <small className="text-red-600">{errors.otherLocation}</small>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="username">Région (obligatoire)</label>

                <AutoComplete
                  ref={(el) => (autoCompleteRefs.current.region = el)}
                  value={selectedRegion}
                  className="w-full"
                  suggestions={filteredRegions}
                  completeMethod={(e) =>
                    searchAutoComplete(e, dataRegion, setFilteredRegions)
                  }
                  onChange={handleChangeRegion}
                  onFocus={() =>
                    handleFocus("region", dataRegion, setFilteredRegions)
                  }
                  dropdown
                  field="name"
                  placeholder="---Région---"
                  showEmptyMessage={
                    loadingRegion === FetchStatusType.LOADING ||
                    loadingRegion === FetchStatusType.FAILED
                  }
                  emptyMessage={
                    loadingRegion === FetchStatusType.FAILED
                      ? "Erreur de chargement"
                      : "Chargement en cours"
                  }
                  dropdownIcon={
                    loadingRegion === FetchStatusType.LOADING ? (
                      <i className="pi pi-spin pi-spinner" />
                    ) : (
                      <i
                        className="pi pi-angle-down"
                        style={{ fontSize: "1.2rem" }}
                      />
                    )
                  }
                />
              </div>
              {errors.region && (
                <small className="text-red-600">{errors.region}</small>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="username">District (obligatoire)</label>

                <AutoComplete
                  ref={(el) => (autoCompleteRefs.current.district = el)}
                  value={selectedDistrict}
                  className="w-full"
                  suggestions={filteredDistricts}
                  completeMethod={(e) =>
                    searchAutoComplete(e, dataDistrict, setFilteredDistricts)
                  }
                  onChange={handleChangeDistrict}
                  onFocus={() =>
                    handleFocus("district", dataDistrict, setFilteredDistricts)
                  }
                  dropdown
                  field="name"
                  placeholder="---District---"
                  disabled={districtLoader === FetchStatusType.LOADING}
                  showEmptyMessage={
                    districtLoader === FetchStatusType.LOADING ||
                    districtLoader === FetchStatusType.FAILED
                  }
                  dropdownIcon={
                    districtLoader === FetchStatusType.LOADING ? (
                      <i className="pi pi-spin pi-spinner" />
                    ) : (
                      <i
                        className="pi pi-angle-down"
                        style={{ fontSize: "1.2rem" }}
                      />
                    )
                  }
                  emptyMessage={
                    districtLoader === FetchStatusType.FAILED
                      ? "Erreur de chargement"
                      : "Chargement en cours"
                  }
                />
              </div>
              {errors.district && (
                <small className="text-red-600">{errors.district}</small>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="username">Commune (obligatoire)</label>

                <AutoComplete
                  ref={(el) => (autoCompleteRefs.current.commune = el)}
                  value={selectedCommune}
                  className="w-full"
                  suggestions={filteredCommunes}
                  completeMethod={(e) =>
                    searchAutoComplete(e, dataCommune, setFilteredCommunes)
                  }
                  onChange={handleChangeCommune}
                  onFocus={() =>
                    handleFocus("commune", dataCommune, setFilteredCommunes)
                  }
                  dropdown
                  field="name"
                  placeholder="---Commune---"
                  disabled={communeLoader === FetchStatusType.LOADING}
                  showEmptyMessage={
                    communeLoader === FetchStatusType.LOADING ||
                    communeLoader === FetchStatusType.FAILED
                  }
                  emptyMessage={
                    communeLoader === FetchStatusType.FAILED
                      ? "Erreur de chargement"
                      : "Chargement en cours"
                  }
                  dropdownIcon={
                    communeLoader === FetchStatusType.LOADING ? (
                      <i className="pi pi-spin pi-spinner" />
                    ) : (
                      <i
                        className="pi pi-angle-down"
                        style={{ fontSize: "1.2rem" }}
                      />
                    )
                  }
                />
              </div>
              {errors.commune && (
                <small className="text-red-600">{errors.commune}</small>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="username">Fokontany (obligatoire)</label>
                <AutoComplete
                  ref={(el) => (autoCompleteRefs.current.fokontany = el)}
                  value={selectedFokontany}
                  className="w-full"
                  suggestions={filteredFokontanys}
                  completeMethod={(e) =>
                    searchAutoComplete(e, dataFokontany, setFilteredFokontanys)
                  }
                  onChange={handleChangeFokontany}
                  onFocus={() =>
                    handleFocus(
                      "fokontany",
                      dataFokontany,
                      setFilteredFokontanys
                    )
                  }
                  dropdown
                  field="name"
                  placeholder="---Fokontany---"
                  showEmptyMessage={
                    fokotanyLoader === FetchStatusType.LOADING ||
                    fokotanyLoader === FetchStatusType.FAILED
                  }
                  emptyMessage={
                    fokotanyLoader === FetchStatusType.FAILED
                      ? "Erreur de chargement"
                      : "Chargement en cours"
                  }
                  dropdownIcon={
                    fokotanyLoader === FetchStatusType.LOADING ? (
                      <i className="pi pi-spin pi-spinner" />
                    ) : (
                      <i
                        className="pi pi-angle-down"
                        style={{ fontSize: "1.2rem" }}
                      />
                    )
                  }
                  disabled={fokotanyLoader === FetchStatusType.LOADING}
                />
                {errors.fokontany && (
                  <small className="text-red-600">{errors.fokontany}</small>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex pt-4 gap-3 justify-content-end">
        <div className="flex pt-4 justify-content-end">
          <Button
            label="Précédent"
            className="btn btn-secondary w-[120px]"
            onClick={handlePreviousStep}
          />
        </div>
        <div className="flex pt-4 justify-content-end">
          <Button
            label="Suivant"
            className="btn btn-primary w-[120px]"
            onClick={handleNextStep}
          />
        </div>
      </div>
    </div>
  );
};

export default CaseDetail;
